import { useState } from "react";
import { Button, Input, Label, Select } from "@/components/ui/form";
import { SectionAddButton } from "@/components/SectionAddButton";
import { type RfqAttachment } from "@/components/RfqDocumentsSection";
import { apiFetch } from "@/utils/api";
import { openAttachmentView } from "@/utils/attachments";

export function RfqSellerQuotesSection({
  rfqId,
  sellerQuotes,
  sellers,
  attachments,
  quotesReadyForApproval,
  status,
  token,
  showAddSeller,
  onToggleAddSeller,
  onRefresh,
}: {
  rfqId: string;
  sellerQuotes: Record<string, unknown>[];
  sellers: { id: string; company_name: string; email: string }[];
  attachments: RfqAttachment[];
  quotesReadyForApproval: boolean;
  status: string;
  token: string | null;
  showAddSeller: boolean;
  onToggleAddSeller: () => void;
  onRefresh: () => Promise<void>;
}) {
  const [showUploadQuote, setShowUploadQuote] = useState(false);
  const [selectedSellers, setSelectedSellers] = useState<string[]>([]);
  const [quoteSellerId, setQuoteSellerId] = useState("");
  const [uploadingQuote, setUploadingQuote] = useState(false);

  const quoteAttachmentsFor = (sellerQuoteId: string) =>
    attachments.filter(
      (a) => a.entity_type === "seller_quote" && a.entity_id === sellerQuoteId && a.source === "uploaded",
    );

  const assignSellers = async () => {
    await apiFetch(`/rfqs/${rfqId}/sellers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sellerIds: selectedSellers }),
    });
    onToggleAddSeller();
    setSelectedSellers([]);
    await onRefresh();
  };

  const markSent = async () => {
    const ids = sellerQuotes
      .filter((sq) => selectedSellers.includes(String(sq.seller_id)))
      .map((sq) => String(sq.id));
    await apiFetch(`/rfqs/${rfqId}/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sellerQuoteIds: ids.length ? ids : sellerQuotes.map((sq) => String(sq.id)),
      }),
    });
    await onRefresh();
  };

  const selectWinner = async (sellerId: string) => {
    await apiFetch(`/rfqs/${rfqId}/winner`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sellerId }),
    });
    await onRefresh();
  };

  const uploadQuote = async (file: File) => {
    if (!quoteSellerId) return;
    setUploadingQuote(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiFetch(`/rfqs/${rfqId}/quotes/${quoteSellerId}/upload`, { method: "POST", body: fd });
      setShowUploadQuote(false);
      setQuoteSellerId("");
      await onRefresh();
    } finally {
      setUploadingQuote(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      {status === "quotes_received" && !quotesReadyForApproval && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Upload at least one seller quote and select the company that sent it before marking customer approved.
        </div>
      )}

      {sellerQuotes.length === 0 ? (
        <p className="text-sm text-argon-secondary">No sellers assigned yet. Use Add to assign sellers.</p>
      ) : (
        sellerQuotes.map((sq) => {
          const sellerQuoteId = String(sq.id);
          const files = quoteAttachmentsFor(sellerQuoteId);
          const hasUpload = files.length > 0;

          return (
            <div
              key={sellerQuoteId}
              className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50/50"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <strong>{String(sq.seller_name)}</strong>
                  <span className="text-sm text-gray-500 ml-2">({String(sq.status)})</span>
                </div>
                {hasUpload && sq.status === "received" && (
                  <Button variant="secondary" size="sm" onClick={() => void selectWinner(String(sq.seller_id))}>
                    Select Winner
                  </Button>
                )}
              </div>

              {hasUpload ? (
                <ul className="space-y-1">
                  {files.map((file) => (
                    <li key={file.id}>
                      <button
                        type="button"
                        className="text-sm font-semibold text-argon-primary hover:underline"
                        onClick={() =>
                          void openAttachmentView(file.id, file.file_name, file.content_type ?? "", token)
                        }
                      >
                        {file.file_name}
                      </button>
                      {file.created_at ? (
                        <span className="text-xs text-argon-secondary ml-2">· {file.created_at}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-argon-secondary">No quote uploaded yet.</p>
              )}
            </div>
          );
        })
      )}

      {showAddSeller && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-3 bg-gray-50/40">
          <p className="text-sm font-semibold text-argon-dark">Add sellers to RFQ</p>
          <div className="flex flex-wrap gap-2">
            {sellers.map((s) => (
              <label key={s.id} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={selectedSellers.includes(s.id)}
                  onChange={(e) => {
                    setSelectedSellers(
                      e.target.checked
                        ? [...selectedSellers, s.id]
                        : selectedSellers.filter((id) => id !== s.id),
                    );
                  }}
                />
                {s.company_name}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void assignSellers()}>Add Selected Sellers</Button>
            <Button variant="secondary" onClick={() => void markSent()}>Mark RFQ Sent</Button>
          </div>
        </div>
      )}

      {sellerQuotes.length > 0 && (
        <div className="flex items-end justify-between gap-2 flex-wrap">
          <div className="text-sm text-argon-secondary">
            {sellerQuotes.length} seller{sellerQuotes.length === 1 ? "" : "s"} on this RFQ
          </div>
          <SectionAddButton
            label="Upload Quote"
            open={showUploadQuote}
            onClick={() => setShowUploadQuote(!showUploadQuote)}
          />
        </div>
      )}

      {showUploadQuote && sellerQuotes.length > 0 && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-3 bg-gray-50/40">
          <p className="text-sm font-semibold text-argon-dark">Upload seller quote</p>
          <p className="text-xs text-argon-secondary">
            Select the company that sent the quote, then upload their quote document.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
            <div className="space-y-1">
              <Label>Company</Label>
              <Select value={quoteSellerId} onChange={(e) => setQuoteSellerId(e.target.value)} required>
                <option value="">Select seller</option>
                {sellerQuotes.map((sq) => (
                  <option key={String(sq.id)} value={String(sq.id)}>
                    {String(sq.seller_name)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Quote file</Label>
              <Input
                type="file"
                disabled={!quoteSellerId || uploadingQuote}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    void uploadQuote(file).finally(() => {
                      e.target.value = "";
                    });
                  }
                }}
              />
            </div>
          </div>
          {uploadingQuote && <p className="text-sm text-argon-secondary">Uploading…</p>}
        </div>
      )}
    </div>
  );
}

export function RfqSellerQuotesAddButton({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return <SectionAddButton open={open} onClick={onClick} />;
}
